import { MapPin, Navigation } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { CONTACT_DETAILS, mapsDirectionsHref, mapsEmbedSrc } from '../contact-details';

/**
 * Google Maps embed, lazy-loaded: the (heavy, third-party) iframe is only
 * mounted once the container scrolls near the viewport, so it never blocks
 * initial page load. A glassmorphism overlay carries the address + a custom
 * marker treatment on top of the rounded map container.
 */
export function ContactMap() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div
      ref={ref}
      className="group relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-border bg-muted shadow-lg sm:aspect-[21/9]"
    >
      {visible ? (
        <iframe
          title={`Map showing ${CONTACT_DETAILS.map.label}`}
          src={mapsEmbedSrc()}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="absolute inset-0 size-full grayscale-[35%] transition-[filter] duration-slow ease-out-expo group-hover:grayscale-0"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <MapPin className="size-8 animate-pulse" aria-hidden="true" />
          <span className="sr-only">Loading map…</span>
        </div>
      )}

      {/* Glassmorphism overlay card with the office address + directions CTA. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 sm:inset-y-0 sm:right-auto sm:flex sm:w-80 sm:items-center sm:p-6">
        <div className="glass-strong pointer-events-auto rounded-xl p-4 transition-transform duration-fast ease-out-expo group-hover:-translate-y-1 sm:p-5">
          <div className="flex items-center gap-2 text-primary">
            <MapPin className="size-5" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground">
              {CONTACT_DETAILS.map.label}
            </span>
          </div>
          <address className="mt-2 not-italic text-sm leading-relaxed text-muted-foreground">
            {CONTACT_DETAILS.addressLines.slice(1).join(', ')}
          </address>
          <a
            href={mapsDirectionsHref()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-primary transition-transform duration-fast ease-out-expo hover:-translate-y-0.5"
          >
            <Navigation className="size-4" aria-hidden="true" />
            Directions
          </a>
        </div>
      </div>
    </div>
  );
}
